import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { DATE_STRING, EMPTY_ARRAY, endOfDay, formatDateString, noop, truncateToDateString } from "@regrello/core-utils";
import type {
  CreateFieldInstanceValueInputs,
  FieldFields,
  FieldInstanceFields,
  PropertyTypeFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldValidationTypeFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
  UpdateFieldInstanceValueInputs,
} from "@regrello/graphql-api";
import type { ColDef, ValueFormatterParams, ValueParserParams } from "ag-grid-community";
import { format, isValid, parse } from "date-fns";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import type { RowDatum } from "../../../../../../ui2/forms/instance/utils/tableV2Utils";
import { getErrorMessageWithPayload } from "../../../../../../utils/getErrorMessageWithPayload";
import {
  SUPPORTED_DATE_FORMATS,
  SUPPORTED_WITH_OPTIONAL_DATE_FORMATS,
} from "../../../customFields/plugins/DateFieldPlugin";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  GetCreateFieldInstanceValueInputsFromFormValueParams,
  ValidateBulkUploadCellValueOptions,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import {
  type ValidationResult,
  validateRequired,
} from "../../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { RegrelloControlledFormFieldSelect } from "../../../formFields/controlled/RegrelloControlledFormFieldSelect";
import { RegrelloFormFieldDate } from "../../../formFields/RegrelloFormFieldDate";
import type { ProspectiveSpectrumFieldVersion } from "../../types/ProspectiveSpectrumFieldVersion";
import { SpectrumFieldPluginDecorator } from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import {
  FrontendValueConstraintRuleName,
  ON_OR_AFTER_TODAY_ARG,
  ON_OR_BEFORE_TODAY_ARG,
  validateByConstraints,
} from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellDate } from "./_internal/RegrelloEditableCellDate";
import type { DateFieldPluginFrontendValue } from "./_internal/types";

const ERROR_INVALID_FORM_VALUE = "Provided 'date'-field form value is not a valid date string";

type VALUE = DateFieldPluginFrontendValue; // Saves some characters without using a troublesome generic type.

export class SpectrumDateFieldPluginDecorator extends SpectrumFieldPluginDecorator<VALUE> {
  constructor(plugin: CustomFieldPlugin<VALUE>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.date";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.TIMESTAMP;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === "TIME");
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.TIMESTAMP,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(valueConstraints: SpectrumValueConstraintFields[]) {
    return valueConstraints.filter(
      ({ valueConstraintRule }) =>
        valueConstraintRule === FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE,
    );
  }

  public override getCreateFieldInstanceValueInputsFromFormValue = (
    inputs: GetCreateFieldInstanceValueInputsFromFormValueParams,
  ): CreateFieldInstanceValueInputs => {
    const { displayOrder, field, inputType, spectrumFieldVersion, value } = inputs;
    if (!isValueValid(value)) {
      throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FORM_VALUE, { field, inputType, value }));
    }

    if (value == null) {
      return {
        fieldId: field.id,
        timeValue: undefined,
        dateValue: undefined,
        inputType,
        displayOrder,
        spectrumFieldVersionId: spectrumFieldVersion?.id,
      };
    }

    return {
      fieldId: field.id,
      timeValue: value != null ? endOfDay(value).toISOString() : undefined,
      dateValue: value != null ? truncateToDateString(value) : undefined,
      inputType,
      displayOrder,
      spectrumFieldVersionId: spectrumFieldVersion?.id,
    };
  };

  public getConstraintConditionOperators() {
    return {
      IS_ZERO: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      IS_NOT_ZERO: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      DATE_IS_AFTER_OR_ON: {
        label: t`Is after or on`,
        inputCount: 1,
        isMultiselect: false,
      },
      DATE_IS_BEFORE_OR_ON: {
        label: t`Is before or on`,
        inputCount: 1,
        isMultiselect: false,
      },
      DATE_IS_ON: {
        label: t`Is on same calendar day`,
        inputCount: 1,
        isMultiselect: false,
      },
      DATE_IS_NOT_ON: {
        label: t`Is not on same calendar day`,
        inputCount: 1,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onChange" as const;
  }

  public getConstraintsToDisplay(field: SpectrumFieldVersionFields): SpectrumFieldConstraintFields[] {
    return field.fieldConstraints;
  }

  public override renderSpectrumFormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    return this.renderFormField(props.fieldInstance?.spectrumFieldVersion.field ?? field, props);
  };

  public renderPreviewFormField() {
    return <RegrelloFormFieldDate disabled={true} onChange={noop} size="medium" value={undefined} />;
  }

  public renderValueConstraints(props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    const { constraints, form, disabled } = props;
    if (constraints.length <= 0) {
      return null;
    }
    const options = [
      { constraint: undefined, args: EMPTY_ARRAY },
      { constraint: constraints[0].constraint, args: [ON_OR_AFTER_TODAY_ARG] },
      { constraint: constraints[0].constraint, args: [ON_OR_BEFORE_TODAY_ARG] },
    ];

    return (
      <RegrelloControlledFormFieldSelect
        className="w-full"
        controllerProps={{
          control: form.control,
          name: "valueConstraints.0",
        }}
        disabled={disabled}
        getOptionLabel={(option) => {
          const isOnOrBeforeToday =
            option.constraint?.valueConstraintRule ===
              FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE &&
            option.args.length > 0 &&
            option.args[0] === ON_OR_BEFORE_TODAY_ARG;
          const isOnOrAfterToday =
            option.constraint?.valueConstraintRule ===
              FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE &&
            option.args.length > 0 &&
            option.args[0] === ON_OR_AFTER_TODAY_ARG;

          return isOnOrAfterToday
            ? t`Only dates in the future, including today`
            : isOnOrBeforeToday
              ? t`Only dates in the past, including today`
              : t`All dates`;
        }}
        label={t`Accepted dates`}
        options={options}
      />
    );
  }

  public override parseTableValue(_spectrumFieldVersion: SpectrumFieldVersionFields, valueIn: unknown) {
    if (valueIn == null || typeof valueIn !== "string") {
      return null;
    }
    const parsedDate = new Date(valueIn);
    if (isValid(parsedDate)) {
      return parsedDate;
    }
    return null;
  }

  public getColumnDef<TData extends RowDatum>(
    colDefParams: Partial<ColDef<TData, VALUE>>,
    options: {
      fieldInstance: FieldInstanceFields;
      placeholderRenderer?: (params: ValueFormatterParams<TData, VALUE>) => string;
    },
  ): ColDef<TData, VALUE> {
    return {
      ...colDefParams,
      cellClassRules: {
        ...colDefParams.cellClassRules,
        "regrello-center-aligned-cell": (_) => true,
        "rg-grid-not-editable": (params) => {
          // (max): gray cell out if it is not editable.
          if (params.colDef.editable == null) {
            return true;
          }
          if (typeof params.colDef.editable === "boolean") {
            return !params.colDef.editable;
          }
          return !params.colDef.editable(params);
        },
      },
      cellEditor: RegrelloEditableCellDate,
      cellEditorParams: {
        fieldInstance: options.fieldInstance,
        isDateOnlyFieldEnabled: true,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      valueParser: (params: ValueParserParams<TData, VALUE>) => {
        return this.parseTableValue(options.fieldInstance.spectrumFieldVersion, params.newValue);
      },
      valueFormatter: (params: ValueFormatterParams<TData, VALUE>) => {
        if (params.value == null) {
          return "";
        }
        if (params.value instanceof Date) {
          return params.value.toISOString();
        }
        return "";
      },
      cellRenderer: (params: ValueFormatterParams<TData, VALUE>): ReactNode => {
        // @ts-expect-error the types shouldn't allow value to be a string, but I'm not trusting types 100% yet. Leaving it in.
        if (params.value == null || params.value === "") {
          return options.placeholderRenderer ? options.placeholderRenderer(params) : "";
        }
        if (params.value instanceof Date) {
          return formatDateString(params.value);
        }
        return String(params.value);
      },
    };
  }

  public getFieldUpdateInputsFromFrontendValue(
    fieldInstance: FieldInstanceFields,
    frontendValue: VALUE,
  ): UpdateFieldInstanceValueInputs {
    return {
      id: fieldInstance.values[0].id,
      inputType: fieldInstance.values[0].inputType,
      timeValue: frontendValue != null ? endOfDay(frontendValue).toISOString() : undefined,
      dateValue: frontendValue != null ? truncateToDateString(frontendValue) : undefined,
    };
  }

  public isValidTableColumn(_: SpectrumFieldVersionFields): boolean {
    return true;
  }

  public validateBulkUploadCellValue(
    _field: FieldFields,
    value: string,
    options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult {
    const valueAsString = value.trim();

    const result = validateRequired(valueAsString, options.isRequired);
    if (result != null) {
      return result;
    }

    const now = new Date();

    const dateFormats = options.dateFieldOptions?.allowAdditionalDateFormats
      ? SUPPORTED_WITH_OPTIONAL_DATE_FORMATS
      : SUPPORTED_DATE_FORMATS;

    const matchedFormatString = dateFormats.find((formatString) => {
      return isValid(parse(valueAsString, formatString, now));
    });

    if (matchedFormatString == null) {
      const commaSeparatedDateFormats = SUPPORTED_DATE_FORMATS.map(
        (dateFormatString) => `'${format(now, dateFormatString)}'`,
      ).join(", ");
      return {
        value: valueAsString,
        info: [
          {
            level: "error",
            message: t`Please enter dates in one of the following formats: ${commaSeparatedDateFormats}`,
          },
        ],
      };
    }

    const parsedDate = parse(valueAsString, matchedFormatString, now);

    const spectrumValidationResult = this.validateValueInternal(options.fieldVersion, parsedDate);
    if (!spectrumValidationResult.isValid) {
      return {
        value: format(parsedDate, DATE_STRING),
        info: spectrumValidationResult.errors.map((errorString) => ({
          message: errorString,
          level: "error" as const,
        })),
      };
    }

    return {
      value: format(parsedDate, DATE_STRING),
    };
  }

  public validateValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: unknown,
    isRequired: boolean,
  ): { isValid: boolean; errors: string[] } {
    if (value == null) {
      if (isRequired) {
        return {
          isValid: false,
          errors: [t`This field is required`],
        };
      }
      return {
        isValid: true,
        errors: EMPTY_ARRAY,
      };
    }

    if (typeof value !== "string" && !(value instanceof Date)) {
      return {
        isValid: false,
        errors: [t`Must be a date`],
      };
    }

    let dateValue: Date;

    if (typeof value === "string") {
      dateValue = new Date(value);
      if (!isValid(dateValue)) {
        return {
          isValid: false,
          errors: [t`Must be a valid date`],
        };
      }
    } else {
      dateValue = value;
    }

    return this.validateValueInternal(fieldVersion, dateValue);
  }

  private validateValueInternal(
    fieldVersion: SpectrumFieldVersionFields,
    value: VALUE,
  ): { isValid: boolean; errors: string[] } {
    if (value == null) {
      return {
        isValid: true,
        errors: EMPTY_ARRAY,
      };
    }
    return validateByConstraints(fieldVersion.fieldConstraints, value);
  }
}

function isValueValid(value: unknown): value is VALUE {
  return value == null || (value instanceof Date && isValid(value));
}
